import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, Building2, Save, Bell, User, Send, UserCheck } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Avatar from "../components/Avatar";
import { confirmDialog } from "../lib/confirm";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import TeamPinSection from "../components/team/TeamPinSection";
import { ViewResetSection } from "../components/ViewResetSection";

// ---------------------------------------------------------------- Company editor
const COMPANY_FIELDS = [
  { key: "name",       label: "Firmenname (voll)",     placeholder: "Weissblom Agentur für Objektbetreuung" },
  { key: "short_name", label: "Kurzname",              placeholder: "Weissblom" },
  { key: "street",     label: "Straße + Hausnummer",   placeholder: "Pfaffensteig 22" },
  { key: "zip_city",   label: "PLZ + Ort",             placeholder: "82041 Oberbiberg" },
  { key: "country",    label: "Land",                  placeholder: "Deutschland" },
  { key: "phone",      label: "Telefon",               placeholder: "+49 155 662 002 85" },
  { key: "email",      label: "E-Mail",                placeholder: "info@weissblom.de" },
  { key: "web",        label: "Website",               placeholder: "www.weissblom.de" },
  { key: "court",      label: "Amtsgericht",           placeholder: "Amtsgericht München" },
  { key: "vat_id",     label: "USt.-ID",               placeholder: "DE367275359" },
  { key: "tax_number", label: "Steuernummer",          placeholder: "145/117/41026" },
  { key: "director",   label: "Geschäftsführung",      placeholder: "R. Keersmaekers" },
  { key: "bank_name",  label: "Bank",                  placeholder: "Deutsche Kreditbank" },
  { key: "iban",       label: "IBAN",                  placeholder: "DE60 1203 0000 0201 6643 47" },
  { key: "bic",        label: "BIC",                   placeholder: "BYLADEM1001" },
];

function CompanySettingsSection({ userRole }) {
  const isAdmin = userRole === "admin";
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/company-settings");
        setValues(data);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
        setValues({});
      }
    })();
  }, []);

  const upd = (k, v) => { setValues((cur) => ({ ...(cur || {}), [k]: v })); setDirty(true); };

  const save = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const payload = {};
      for (const f of COMPANY_FIELDS) payload[f.key] = (values?.[f.key] ?? "").trim();
      const { data } = await api.put("/team/company-settings", payload);
      setValues(data);
      setDirty(false);
      toast.success("Firmen-Stammdaten gespeichert. Neue Rechnungen nutzen ab sofort diese Werte.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const sendReminders = async () => {
    if (!(await confirmDialog({
      title: "Zahlungserinnerungen senden?",
      description:
        "Weissblom sucht jetzt alle Rechnungen, die seit ≥ 7 Tagen fällig und nicht bezahlt sind, " +
        "und verschickt eine freundliche Zahlungserinnerung per E-Mail. Jede Rechnung wird nur einmal erinnert.",
      confirmText: "Jetzt senden",
    }))) return;
    setReminderBusy(true);
    try {
      const { data } = await api.post("/team/invoices/send-reminders");
      toast.success(
        `Fertig · ${data.sent} Erinnerungen verschickt · ${data.skipped} übersprungen (keine E-Mail) · ${data.errors} Fehler.`
      );
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setReminderBusy(false); }
  };

  if (values === null) {
    return (
      <section className="bg-white border border-navy/10 p-6 md:p-8 mt-6" data-testid="team-profile-company-loading">
        <div className="label-caps">Firmen-Stammdaten werden geladen …</div>
      </section>
    );
  }

  return (
    <section className="bg-white border border-navy/10 p-6 md:p-8 mt-6" data-testid="team-profile-company">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}>
          <Building2 className="w-4 h-4 text-navy" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Firmen-Stammdaten</div>
          <h2 className="font-display text-2xl text-navy leading-tight">Weissblom · Rechnungs-Kopf & Fuß</h2>
          <p className="text-[13px] text-navy-dark/65 mt-2 max-w-2xl leading-relaxed">
            Diese Werte tauchen im Kopf, in der Bankverbindung, im SEPA-Girocode und im rechtlichen Fuß jeder Rechnung auf.
            Änderungen wirken ab der nächsten Rechnung.
            {!isAdmin && <> — <b>nur Admins</b> können Änderungen speichern.</>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {COMPANY_FIELDS.map((f) => (
          <div key={f.key} className={f.key === "name" ? "md:col-span-2" : ""}>
            <label htmlFor={`company-${f.key}`} className="block text-[11px] text-navy-dark/60 mb-1">{f.label}</label>
            <input
              id={`company-${f.key}`}
              type="text"
              disabled={!isAdmin}
              value={values?.[f.key] ?? ""}
              onChange={(e) => upd(f.key, e.target.value)}
              placeholder={f.placeholder}
              data-testid={`company-field-${f.key}`}
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy-dark disabled:bg-ivory/40 disabled:cursor-not-allowed"
            />
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-navy/10 pt-5">
        <p className="text-[11.5px] text-navy-dark/55 italic max-w-md">
          Tipp: Der Kurzname erscheint oben links neben Straße und PLZ, der volle Firmenname im 4-spaltigen Rechnungsfuß.
        </p>
        {isAdmin && (
          <button
            onClick={save}
            disabled={busy || !dirty}
            data-testid="company-save-btn"
            className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
            style={{ background: BRAND_COLORS.navy }}
          >
            <Save className="w-4 h-4" strokeWidth={1.5} />
            {busy ? "Speichere …" : dirty ? "Änderungen speichern" : "Gespeichert"}
          </button>
        )}
      </div>

      {/* --- Zahlungserinnerung ---------------------------------------- */}
      {isAdmin && (
        <div className="mt-6 border-t border-navy/10 pt-5" data-testid="team-profile-reminders">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: withAlpha(BRAND_COLORS.gold, 0.14) }}>
              <Bell className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            </div>
            <div>
              <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Zahlungserinnerungen</div>
              <h3 className="font-display text-lg text-navy">Automatischer Reminder-Mail-Versand</h3>
              <p className="text-[12.5px] text-navy-dark/65 mt-1 max-w-2xl leading-relaxed">
                Weissblom prüft jede Nacht automatisch alle Rechnungen. Ist eine Rechnung ≥ 7 Tage nach dem Fälligkeitsdatum
                immer noch nicht als „Bezahlt" markiert, verschickt das System eine freundliche Erinnerung an den Kunden.
                Jede Rechnung wird höchstens einmal erinnert.
              </p>
            </div>
          </div>
          <button
            onClick={sendReminders}
            disabled={reminderBusy}
            data-testid="send-reminders-now-btn"
            className="inline-flex items-center gap-2 text-navy border border-navy/25 hover:border-gold px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60"
          >
            <Bell className="w-4 h-4" strokeWidth={1.5} />
            {reminderBusy ? "Sende …" : "Erinnerungen jetzt manuell senden"}
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Personal editor
function PersonalDetailsSection({ target, targetIsSelf, refreshTarget }) {
  // Feb 2026 · Bearbeitet entweder das eigene Profil (Concierge · echter Login
  // oder Admin ohne Impersonation) ODER das Profil des impersonated Concierges
  // (Admin sieht Charlie · edited Charlie).
  const [name, setName] = useState("");
  const [chatId, setChatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(target?.name || "");
    setChatId(target?.telegram_chat_id || "");
    setDirty(false);
  }, [target?.id, target?.name, target?.telegram_chat_id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if ((name || "").trim() !== (target?.name || "")) payload.name = (name || "").trim();
      const cleanChat = (chatId || "").trim() || null;
      if (cleanChat !== (target?.telegram_chat_id || null)) payload.telegram_chat_id = cleanChat;
      if (Object.keys(payload).length === 0) {
        toast.info("Keine Änderungen zu speichern.");
        setSaving(false);
        return;
      }
      if (targetIsSelf) {
        await api.patch("/me/preferences", payload);
      } else {
        await api.patch(`/team/members/${target.id}/profile`, payload);
      }
      await refreshTarget();
      setDirty(false);
      toast.success(targetIsSelf ? "Profil gespeichert." : `${target.name || "Concierge"} · Profil gespeichert.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const testTelegram = async () => {
    // Nur für den eigenen Login sinnvoll — der Admin kann keinen Test-Push
    // im Namen eines anderen Users triggern (Backend nutzt JWT-User).
    if (!targetIsSelf) {
      toast.info("Test-Push kann nur vom eingeloggten Nutzer selbst ausgelöst werden.");
      return;
    }
    setTesting(true);
    try {
      await api.post("/me/telegram/test");
      toast.success("Test-Push gesendet — bitte Telegram öffnen.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Fehler beim Senden.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section
      className="bg-white border border-navy/10 p-6 md:p-8 mt-6"
      data-testid="team-profile-personal"
      data-target-user={target?.id || ""}
      data-target-self={targetIsSelf ? "true" : "false"}
    >
      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}
        >
          <User className="w-4 h-4 text-navy" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>
            Persönliche Angaben
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight">
            {targetIsSelf ? "So sehen dich Kunden und Team" : `Profil · ${target?.name || "Concierge"}`}
          </h2>
          <p className="text-[13px] text-navy-dark/65 mt-2 max-w-2xl leading-relaxed">
            {targetIsSelf
              ? "Dein Name erscheint neben deinem Profilfoto — beim Kunden im Kalender-Termin, in Anfragen und im Anzeige-Header. Die Telegram-Chat-ID aktiviert Push-Benachrichtigungen für neue Anfragen deiner Objekte."
              : "Als Admin bearbeitest du gerade das Profil eines Concierges. Änderungen an Name, Telegram-Chat-ID oder Profilfoto werden dem gewählten Concierge zugeordnet — nicht deinem Admin-Konto."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="personal-name" className="block text-[11px] text-navy-dark/60 mb-1">
            Anzeigename
          </label>
          <input
            id="personal-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            placeholder="z. B. Charlotte Gehrmann"
            data-testid="team-profile-name-input"
            className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy-dark"
          />
        </div>
        <div>
          <label htmlFor="personal-email" className="block text-[11px] text-navy-dark/60 mb-1">
            E-Mail (nicht änderbar)
          </label>
          <input
            id="personal-email"
            type="email"
            value={target?.email || ""}
            disabled
            data-testid="team-profile-email-input"
            className="w-full border border-navy/20 px-3 py-2.5 text-[13px] text-navy-dark/60 bg-ivory/40 cursor-not-allowed"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="personal-telegram" className="block text-[11px] text-navy-dark/60 mb-1">
            Telegram-Chat-ID (optional)
          </label>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
            <input
              id="personal-telegram"
              type="text"
              value={chatId}
              onChange={(e) => { setChatId(e.target.value); setDirty(true); }}
              placeholder="z. B. 959862760"
              data-testid="team-profile-telegram-input"
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy-dark"
            />
            <button
              type="button"
              onClick={testTelegram}
              disabled={testing || !target?.telegram_chat_id || !targetIsSelf}
              data-testid="team-profile-telegram-test-btn"
              title={!targetIsSelf ? "Test-Push kann nur der eingeloggte Nutzer selbst auslösen." : undefined}
              className="inline-flex items-center gap-2 border border-gold text-navy px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-gold/10 disabled:opacity-40 whitespace-nowrap"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
              {testing ? "Sende …" : "Test-Push"}
            </button>
          </div>
          <p className="text-[11px] text-navy-dark/50 mt-2">
            Chat-ID finden: Öffne{" "}
            <a
              href="https://t.me/Weissblom_concierge_bot"
              target="_blank"
              rel="noreferrer"
              className="text-navy border-b border-gold"
            >
              @Weissblom_concierge_bot
            </a>
            {" "}in Telegram, klicke START, dann öffne{" "}
            <code className="bg-navy/5 px-1">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>
            {" "}und kopiere den <code className="bg-navy/5 px-1">chat.id</code>-Wert.
          </p>
        </div>
      </div>

      <div className="mt-6 flex justify-end border-t border-navy/10 pt-5">
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid="team-profile-save-btn"
          className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
          style={{ background: BRAND_COLORS.navy }}
        >
          <Save className="w-4 h-4" strokeWidth={1.5} />
          {saving ? "Speichere …" : dirty ? "Änderungen speichern" : "Gespeichert"}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- Team profile page
export default function TeamProfile() {
  const { user, activeConciergeId, refresh } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);

  // Feb 2026 · Wenn der Admin gerade als Concierge unterwegs ist
  // (`activeConciergeId` != eigener id), zeigen und bearbeiten wir das
  // Profil des impersonated Concierges — nicht das eigene. Der echte
  // Concierge-Login zeigt weiterhin sein eigenes Profil (activeConciergeId
  // === user.id in dem Fall).
  const isImpersonating =
    user?.role === "admin" &&
    !!activeConciergeId &&
    activeConciergeId !== user.id;
  const targetId = isImpersonating ? activeConciergeId : user?.id || null;
  const targetIsSelf = !isImpersonating;

  const loadTarget = useCallback(async () => {
    if (!targetId) return;
    try {
      if (targetIsSelf) {
        // Der aktuelle User im Auth-Kontext hat schon alle Felder.
        setTarget(user);
      } else {
        const { data } = await api.get(`/team/members/${targetId}/profile`);
        setTarget(data);
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setTarget(null);
    }
  }, [targetId, targetIsSelf, user]);

  useEffect(() => {
    loadTarget();
  }, [loadTarget]);

  const refreshTarget = useCallback(async () => {
    if (targetIsSelf) {
      await refresh();
    } else {
      await loadTarget();
    }
  }, [targetIsSelf, refresh, loadTarget]);

  const currentFileId = target?.avatar_file_id || null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Bitte eine Bilddatei wählen."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Bild ist zu groß (max. 8 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const url = targetIsSelf ? "/me/avatar" : `/team/members/${targetId}/avatar`;
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshTarget();
      toast.success("Profilfoto aktualisiert.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!(await confirmDialog({
      title: "Profilfoto entfernen?",
      description: targetIsSelf
        ? "Ihr Profilfoto wird gelöscht — Kunden sehen dann Ihre Initialen."
        : `${target?.name || "Concierge"}s Profilfoto wird gelöscht — Kunden sehen dann die Initialen.`,
      confirmText: "Entfernen",
      variant: "danger",
    }))) return;
    setBusy(true);
    try {
      const url = targetIsSelf ? "/me/avatar" : `/team/members/${targetId}/avatar`;
      await api.delete(url);
      await refreshTarget();
      toast.success("Profilfoto entfernt.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14"
      data-testid="team-profile"
      data-target-user={targetId || ""}
    >
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {targetIsSelf ? "Ihr Profil" : `${target?.name || "Profil"} · Concierge`}
        </h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-lg">
          {targetIsSelf
            ? "Ihr Foto erscheint beim Kunden, sobald Sie eine Anfrage übernehmen. Ein bekanntes Gesicht — sofort persönlich."
            : "Du bearbeitest das Profil eines Concierges. Alles was du hier änderst, gilt für den gewählten Concierge — nicht für dein Admin-Konto."}
        </p>
        {!targetIsSelf && (
          <div
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
            data-testid="team-profile-impersonation-hint"
          >
            <UserCheck className="w-4 h-4" strokeWidth={1.6} />
            <span className="text-[11px] tracking-[0.15em] uppercase">
              Concierge-Impersonation aktiv · Änderungen gelten für {target?.name || "diesen Concierge"}
            </span>
          </div>
        )}
      </div>

      <section className="bg-white border border-navy/10 p-6 md:p-8">
        <div className="flex items-center gap-6 mb-8">
          <Avatar
            name={target?.name}
            fileId={currentFileId}
            size={96}
            testid="team-profile-current-avatar"
          />
          <div>
            <div className="font-display text-2xl text-navy">{target?.name || target?.email || "—"}</div>
            <div className="text-[12px] tracking-wider uppercase text-navy-dark/60 mt-1">{target?.role}</div>
            <div className="text-[12px] text-navy-dark/60 mt-1">{target?.email}</div>
          </div>
        </div>

        <div className="border-t border-navy/10 pt-6">
          <div className="label-caps mb-3">Profilfoto</div>
          <div className="flex flex-wrap gap-3">
            <label
              htmlFor="team-avatar-upload"
              className={`inline-flex items-center gap-2 border border-navy/25 hover:border-gold px-5 py-2.5 cursor-pointer text-[12px] tracking-[0.15em] uppercase text-navy transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="team-avatar-upload-label"
            >
              <UploadCloud className="w-4 h-4" strokeWidth={1.5} />
              {currentFileId ? "Foto ersetzen" : "Foto hochladen"}
              <input
                id="team-avatar-upload"
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={upload}
                disabled={busy}
                data-testid="team-avatar-upload-input"
              />
            </label>
            {currentFileId && (
              <button
                onClick={remove}
                disabled={busy}
                data-testid="team-avatar-remove-btn"
                className="inline-flex items-center gap-2 border border-navy/25 hover:border-red-600 hover:text-red-600 text-navy-dark/70 px-5 py-2.5 text-[12px] tracking-[0.15em] uppercase transition-colors"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Entfernen
              </button>
            )}
          </div>
          <p className="text-[11.5px] text-navy-dark/55 italic mt-3">
            Empfohlen: quadratisches Bild, mindestens 400 × 400 px, max. 8 MB.
          </p>
        </div>
      </section>

      {/* --- Persönliche Angaben (Name + Telegram) --- */}
      {target && (
        <PersonalDetailsSection
          target={target}
          targetIsSelf={targetIsSelf}
          refreshTarget={refreshTarget}
        />
      )}

      {/* --- Firmen-Stammdaten (nur für Team, editierbar nur für Admin) --- */}
      {/* Bei Impersonation NICHT rendern — die Firmen-Stammdaten sind
          system-weit, nicht Concierge-spezifisch, und im Impersonations-
          Kontext irrelevant. */}
      {targetIsSelf && <CompanySettingsSection userRole={user?.role} />}

      {/* --- Persönliche Team-PIN (nur eigene Profilansicht) ---
          Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Abfrage bei Concierge":
          Jeder Team-User verwaltet seine eigene PIN. Bei Impersonation eines
          fremden Concierges verstecken wir die Sektion — sonst könnte ein
          Admin fremde PINs überschreiben. */}
      {targetIsSelf && <TeamPinSection />}

      {targetIsSelf && <TeamViewResetSection />}
    </div>
  );
}

function TeamViewResetSection() {
  const { resetUiPrefs } = useAuth();
  return (
    <div className="mt-6">
      <ViewResetSection
        testId="team-view-reset"
        title="Sidebar und Kalender-Filter auf Standard"
        description="Klappt die Team-Sidebar wieder aus und entfernt gespeicherte Filter-Chips im Kalender. Gilt für dein Konto auf allen Geräten — Daten bleiben unverändert."
        buttonLabel="Ansicht zurücksetzen"
        onReset={() => resetUiPrefs(["sidebar_collapsed", "calendar_filters"])}
      />
    </div>
  );
}
